/**
 * fs-iconv 核心功能模組
 *
 * 這個模組提供了檔案系統操作的編碼轉換功能，可以處理各種文字編碼的檔案讀寫操作。
 *
 * @module fs-iconv/core
 * @author bluelovers
 * @since 2019/3/17
 */

import { vEncoding } from 'iconv-jschardet';
import fsExtra from 'fs-extra';
import { clone } from "lodash";
import iconv from 'iconv-jschardet';
import Bluebird from 'bluebird';
import stream from 'stream';
import { trimFilename } from './util';
import { ITSRequiredWith, ITSRequireAtLeastOne } from 'ts-type';
import { PassThrough } from 'stream';

/** 檔案系統庫符號，用於內部引用原始的 fs-extra 實例 */
export const SymFSLib = Symbol('fsLib');

/**
 * 包裝檔案系統操作並添加編碼轉換功能
 *
 * @template F 檔案系統類型，預設為 fs-extra
 * @param {F} fsLib 要包裝的檔案系統庫
 * @returns {WrapFSIconv.IWrapFS<F>} 包裝後的檔案系統實例
 */
export function WrapFSIconv<F extends typeof fsExtra = typeof fsExtra>(fsLib: F): WrapFSIconv.IWrapFS<F>
{
	// 複製檔案系統庫以避免修改原始實例
	let fs = clone(fsLib) as any as WrapFSIconv.IWrapFS<F>;

	// 將所有函數綁定到新的 fs 實例上
	Object.keys(fs)
		.forEach(k => {
			if (typeof fsLib[k] === 'function')
			{
				fs[k] = fsLib[k].bind(fsLib);
			}
		})
	;

	// 儲存原始檔案系統庫的引用
	fs[SymFSLib] = fsLib;

	// 添加 iconv 編碼轉換功能
	fs.iconv = iconv;

	// 綁定各種檔案操作方法
	fs.ensureWriteStream = ensureWriteStream.bind(fs);
	fs.saveFile = saveFile.bind(fs);
	fs.saveFileSync = saveFileSync.bind(fs);

	fs.loadFileSync = loadFileSync.bind(fs);
	fs.loadFile = loadFile.bind(fs);

	fs._createStreamPassThrough = _createStreamPassThrough.bind(fs);
	fs._outputStream = _outputStream.bind(fs);
	fs._autoDecode = _autoDecode.bind(fs);

	fs.trimFilename = trimFilename;

	// 標記為 ES 模組
	Object.defineProperty(exports, "__esModule", { value: true });

	// 設置預設導出
	// @ts-ignore
	fs.default = fs;

	return fs;
}
/**
 * 確保檔案存在並創建寫入流
 *
 * 這個函數會先確保目標檔案存在（如果不存在則創建），然後返回一個可寫入的檔案流。
 *
 * @param {string} file 檔案路徑
 * @returns {fsExtra.WriteStream} 檔案寫入流
 */
export function ensureWriteStream(file: string)
{
	// 從符號屬性獲取原始的 fs-extra 實例
	// @ts-ignore
	let fs = (this as any as WrapFSIconv.IWrapFS)[SymFSLib] as typeof fsExtra;

	// 確保檔案存在
	fs.ensureFileSync(file);
	return fs.createWriteStream(file);
}
/**
 * 同步保存檔案並處理編碼轉換
 *
 * 這個函數會將資料保存到指定檔案，並在需要時進行編碼轉換。
 *
 * @param {string} file 檔案路徑
 * @param {any} data 要保存的資料
 * @param {WrapFSIconv.IWrapFSIconvOptions} [options={}] 選項
 * @param {vEncoding} [options.encoding] 目標編碼
 * @returns {boolean} 總是返回 true
 */
export function saveFileSync(file: string, data, options: WrapFSIconv.IWrapFSIconvOptions = {})
{
	// 從符號屬性獲取原始的 fs-extra 實例
	// @ts-ignore
	let fs = (this as any as WrapFSIconv.IWrapFS)[SymFSLib] as typeof fsExtra;

	// 確保檔案存在
	fs.ensureFileSync(file);

	// 如果需要編碼轉換，則進行轉換
	if (options.encoding)
	{
		data = iconv.encode(data, options.encoding);
	}

	// 同步寫入檔案
	fs.outputFileSync(file, data);

	return true;
}
/**
 * 異步保存檔案並處理編碼轉換
 *
 * 這個函數會將資料保存到指定檔案，並在需要時進行編碼轉換。使用 Promise 處理異步操作。
 *
 * @param {string} file 檔案路徑
 * @param {any} data 要保存的資料
 * @param {WrapFSIconv.IWrapFSIconvOptions} [options={}] 選項
 * @param {vEncoding} [options.encoding] 目標編碼
 * @returns {Bluebird<boolean>} Promise，解析為 true
 */
export function saveFile(file: string, data, options: WrapFSIconv.IWrapFSIconvOptions = {})
{
	// 獲取當前實例和原始的 fs-extra 實例
	// @ts-ignore
	let self: WrapFSIconv.IWrapFS<typeof fsExtra> = this;
	let fs = self[SymFSLib] as typeof fsExtra;

	return Bluebird
		.resolve(fs.ensureFile(file))
		.tap(function ()
		{
			return new Bluebird(function (resolve, reject)
			{
				// 如果需要編碼轉換，則進行轉換
				if (options.encoding)
				{
					data = iconv.encode(data, options.encoding);
				}

				// 創建讀取流和寫入流
				let readStream = self._createStreamPassThrough(data);
				let writeStream = self._outputStream(file, readStream);

				// 處理流事件
				writeStream.on('error', reject);
				writeStream.on('finish', resolve);
			})
		})
		.thenReturn(true)
		;
}
export declare namespace WrapFSIconv
{
	export type IWrapFS<F extends typeof fsExtra = typeof fsExtra> = F &
	{
		[SymFSLib]: F | typeof fsExtra;

		iconv: typeof iconv;
		ensureWriteStream(file: string): fsExtra.WriteStream;

		// --------------------

		saveFile(file: string, data, options?: WrapFSIconv.IWrapFSIconvOptions): Bluebird<boolean>;

		saveFileSync(file: string, data, options?: WrapFSIconv.IWrapFSIconvOptions): boolean

		// --------------------

		loadFile<T = string>(file: string, options: WrapFSIconv.IWrapFSIconvOptionsLoadFile2): Bluebird<T>;
		loadFile<T = Buffer>(file: string, options?: WrapFSIconv.IWrapFSIconvOptionsLoadFile): Bluebird<T>;

		loadFileSync<T = string>(file: string, options: WrapFSIconv.IWrapFSIconvOptionsLoadFile2): T;
		loadFileSync<T = Buffer>(file: string, options?: WrapFSIconv.IWrapFSIconvOptionsLoadFile): T;

		// --------------------

		_createStreamPassThrough(data: unknown): stream.Readable;
		_outputStream(file: string, readStream: stream.Readable): fsExtra.WriteStream;

		_autoDecode<T>(buf: T, options: WrapFSIconv.IWrapFSIconvOptionsLoadFile & {
			autoDecode: true | string[];
		}): T | string | Buffer;
		_autoDecode(buf: unknown, options: WrapFSIconv.IWrapFSIconvOptionsLoadFile): Buffer;

		// ----------

		trimFilename(name: unknown): string
	}

	export interface IWrapFSIconvOptions
	{
		encoding?: vEncoding;
	}

	export interface IWrapFSIconvOptionsLoadFile
	{
		encoding?: string;
		flag?: string;

		autoDecode?: boolean | string[],
	}

	export type IWrapFSIconvOptionsLoadFile2 = ITSRequireAtLeastOne<IWrapFSIconvOptionsLoadFile, 'encoding' | 'autoDecode'>

	export type IEncoding = vEncoding
}

/**
 * 創建數據流傳遞流
 *
 * 這個函數將創建一個 PassThrough 流，用於在讀取和寫入操作之間傳遞數據。
 *
 * @param {unknown} data 要傳遞的資料
 * @returns {stream.Readable} 可讀取的數據流
 */
export function _createStreamPassThrough(data): stream.Readable
{
	let readStream = new PassThrough();
	readStream.end(data);
	return readStream;
}
/**
 * 創建檔案輸出流
 *
 * 這個函數將創建一個寫入流，用於將數據流寫入指定檔案。
 *
 * @param {string} file 檔案路徑
 * @param {stream.Readable} readStream 要寫入的數據流
 * @returns {fsExtra.WriteStream} 檔案寫入流
 */
export function _outputStream(file: string, readStream: stream.Readable): fsExtra.WriteStream
{
	// 從符號屬性獲取原始的 fs-extra 實例
	// @ts-ignore
	let fs = (this as any as WrapFSIconv.IWrapFS)[SymFSLib] as typeof fsExtra;

	let writeStream = fs.createWriteStream(file);
	readStream.pipe(writeStream);
	return writeStream;
}
/**
 * 自動解碼緩衝區
 *
 * 這個函數會根據提供的選項自動檢測並解碼緩衝區。如果提供了 autoDecode 選項，會嘗試使用指定的編碼進行解碼。
 *
 * @template T 緩衝區類型
 * @param {T} buf 要解碼的緩衝區
 * @param {WrapFSIconv.IWrapFSIconvOptionsLoadFile} options 解碼選項
 * @param {boolean | string[]} [options.autoDecode] 自動解碼選項
 * @param {string} [options.encoding] 目標編碼
 * @returns {T | string | Buffer} 解碼後的結果
 */
export function _autoDecode<T>(buf: T, options: WrapFSIconv.IWrapFSIconvOptionsLoadFile & {
	autoDecode: true | string[],
}): T | string | Buffer
export function _autoDecode(buf, options: WrapFSIconv.IWrapFSIconvOptionsLoadFile): Buffer
export function _autoDecode(buf, options: WrapFSIconv.IWrapFSIconvOptionsLoadFile): string | Buffer
{
	if (Array.isArray(options.autoDecode))
	{
		let _do: string;
		let c = iconv._enc(iconv.detect(buf, true).name);

		for (let from of (options.autoDecode as string[]))
		{
			let cd = iconv.codec_data(from);
			let key: string;

			if (cd && cd.name)
			{
				key = iconv._enc(cd.name);

				if (c === key)
				{
					_do = key;

					break;
				}
			}
		}

		if (_do)
		{
			return iconv.encode(buf, null, options.encoding);
		}
		else
		{
			return buf;
		}
	}

	return iconv.encode(buf);
}
/**
 * 異步載入檔案並處理編碼轉換
 *
 * 這個函數會異步載入指定檔案，並根據提供的選項進行編碼轉換。
 *
 * @template T 返回類型，預設為 string
 * @param {string} file 檔案路徑
 * @param {WrapFSIconv.IWrapFSIconvOptionsLoadFile2} options 載入選項
 * @param {string} [options.encoding] 目標編碼
 * @param {boolean | string[]} [options.autoDecode] 自動解碼選項
 * @returns {Bluebird<T>} Promise，解析為載入的資料
 */
export function loadFile<T = string>(file: string, options: WrapFSIconv.IWrapFSIconvOptionsLoadFile2): Bluebird<T>
export function loadFile<T = Buffer>(file: string, options?: WrapFSIconv.IWrapFSIconvOptionsLoadFile): Bluebird<T>
export function loadFile(file: string, options: WrapFSIconv.IWrapFSIconvOptionsLoadFile = {}): Bluebird<Buffer | string>
{
	// 獲取當前實例和原始的 fs-extra 實例
	// @ts-ignore
	let self: WrapFSIconv.IWrapFS<typeof fsExtra> = this;
	let fs = self[SymFSLib] as typeof fsExtra;

	let ps: Promise<any>;

	if (options.encoding)
	{
		let enc = iconv.isNodeEncoding(options.encoding);

		if (enc)
		{
			ps = fs.readFile(file, options as any);
		}
		else
		{
			let ops: WrapFSIconv.IWrapFSIconvOptionsLoadFile = Object.assign({}, options);
			delete ops.encoding;

			ps = fs.readFile(file, ops as any)
				.then(function (buf)
				{
					return iconv.decode(buf, options.encoding);
				})
			;
		}
	}
	else if (options.autoDecode)
	{
		ps = fs.readFile(file, options as any)
			.then(function (buf)
			{
				return self._autoDecode(buf, options);
			})
		;
	}
	else
	{
		ps = fs.readFile(file, options as any);
	}

	return Bluebird.resolve(ps);
}
/**
 * 同步載入檔案並處理編碼轉換
 *
 * 這個函數會同步載入指定檔案，並根據提供的選項進行編碼轉換。
 *
 * @template T 返回類型，預設為 string
 * @param {string} file 檔案路徑
 * @param {WrapFSIconv.IWrapFSIconvOptionsLoadFile2} options 載入選項
 * @param {string} [options.encoding] 目標編碼
 * @param {boolean | string[]} [options.autoDecode] 自動解碼選項
 * @returns {T} 載入的資料
 */
export function loadFileSync<T = string>(file: string, options: WrapFSIconv.IWrapFSIconvOptionsLoadFile2): T
export function loadFileSync<T = Buffer>(file: string, options?: WrapFSIconv.IWrapFSIconvOptionsLoadFile): T
export function loadFileSync(file: string, options: WrapFSIconv.IWrapFSIconvOptionsLoadFile = {}): Buffer | string
{
	// 獲取當前實例和原始的 fs-extra 實例
	// @ts-ignore
	let self: WrapFSIconv.IWrapFS<typeof fsExtra> = this;
	let fs = self[SymFSLib] as typeof fsExtra;

	let ps;

	if (options.encoding)
	{
		let enc = iconv.isNodeEncoding(options.encoding);

		if (enc)
		{
			// @ts-ignore
			ps = fs.readFileSync(file, options);
		}
		else
		{
			let ops: WrapFSIconv.IWrapFSIconvOptionsLoadFile = Object.assign({}, options);
			delete ops.encoding;

			// @ts-ignore
			ps = iconv.decode(fs.readFileSync(file, ops), options.encoding);
		}
	}
	else if (options.autoDecode)
	{
		// @ts-ignore
		ps = self._autoDecode(fs.readFileSync(file, options), options);
	}
	else
	{
		// @ts-ignore
		ps = fs.readFileSync(file, options);
	}

	return ps;
}
export default exports as typeof import('./core');
